import { ButtonType, ButtonWidget } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { AppState } from '../../../app/state/AppState';
import * as Layout from '../../../common/styles/Layout';
import { ContentDefinitions } from '../../models/contentDefinitions';
import { updateFilter } from '../../actions/UpdateFilterAction';
import { ContentType } from '../../models/ContentType';
import { ContentTypeFilterAllOption, ContentTypeFilterValue } from '../../models/ContentTypeFilterValue';
import { ConnectedContentTypeFilter } from './quickFilter/ContentTypeFilter';
import { ConnectedModifiedTimeFilter } from './quickFilter/ModifiedTimeFilter';
import { ConnectedOwnerFilter } from './quickFilter/OwnerFilter';
import { NavigationService } from '../../services/NavigationService';
import { FilterAllOption, FilterQuery } from '../../state/FilterState';
import { selectFilterQuery } from '../../selectors/selectSingleContentTypeSearchResult';
import { Messages } from '../../../../gen-src/Localize';
import { FilterAction, TelemetryService } from '../../services/TelemetryService';
import { FilterTypes } from '../../models/FilterTypes';
import { SessionManagementService } from '../../services/SessionManagementService';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';
import { ConnectedTagFilter } from './quickFilter/TagFilter';

export const ContentTypeFilterTestID = 'ContentTypeFilter';
export const QuickFilterTestID = 'QuickFilter';
export const ClearFiltersButtonTestID = 'ClearFilters';
export const FilterBarTestID = 'FilterBar';

export type FilterBarProps = FilterBarStateProps & FilterBarDispatchProps;

export function FilterBar(props: FilterBarProps): JSX.Element {
  return (
    <div data-tb-test-id={FilterBarTestID} className={Layout.flexH} role={'region'} aria-label={Messages.FilterBar()}>
      <div data-tb-test-id={ContentTypeFilterTestID}>
        <ConnectedContentTypeFilter />
      </div>
      {renderQuickFilters()}
      <div className={Layout.flexAuto}></div>
    </div>
  );

  function renderQuickFilters(): JSX.Element {
    if (props.contentType === ContentTypeFilterAllOption) {
      return (
        <div className={Layout.flexH}>
          {SessionManagementService.instance.isLimitedUserVisibilityChannel() ? null : <ConnectedOwnerFilter key="0" />}
          <ConnectedModifiedTimeFilter />
          <ConnectedTagFilter />
          {renderClearFilterButton()}
        </div>
      );
    } else {
      return renderQuickFiltersForSingleContentType();
    }
  }

  function renderQuickFiltersForSingleContentType(): JSX.Element {
    const filters = ContentDefinitions[props.contentType as ContentType].quickFilters;
    if (filters) {
      const quickFilters = filters
        .filter((Filter) => Filter !== ConnectedOwnerFilter || !SessionManagementService.instance.isLimitedUserVisibilityChannel())
        .map((Filter, id) => {
          return <Filter key={id} />;
        });

      return (
        <div data-tb-test-id={QuickFilterTestID} className={Layout.flexH}>
          {quickFilters}
          {renderClearFilterButton()}
        </div>
      );
    } else {
      return <></>;
    }
  }

  function renderClearFilterButton(): JSX.Element {
    return isAnyFilterSet() ? (
      <div
        style={{
          borderLeft: 'solid 1px #c8c8c8',
          height: '24px',
          marginLeft: '12px',
          marginTop: '3px',
          paddingLeft: '18px',
        }}
      >
        <ButtonWidget buttonType={ButtonType.LowEmphasis} onClick={onClearFilterClicked} testId={ClearFiltersButtonTestID}>
          {Messages.ClearFilters()}
        </ButtonWidget>
      </div>
    ) : (
      <></>
    );
  }

  function onClearFilterClicked() {
    sendFiltersRemovedTelemetryEvent();
    NavigationService.instance.clearFilter();
  }

  function sendFiltersRemovedTelemetryEvent(): void {
    Object.keys(FilterTypes)
      .filter((type) => props.filter[type as keyof FilterQuery].id !== FilterAllOption)
      .map((val) => {
        TelemetryService.instance.filtersUpdatedEvent(val, FilterAction.Removed, props.contentType, props.searchTerm);
      });
    if (
      props.selectedContentTypeFilterValue !== ContentTypeFilterAllOption &&
      FeatureFlagService.instance.getValue(FeatureFlag.searchProductMetrics)
    ) {
      TelemetryService.instance.filtersUpdatedEvent('contentTypes', FilterAction.Removed, props.contentType, props.searchTerm);
    }
  }

  function isAnyFilterSet(): boolean {
    for (const key in props.filter) {
      const filterValue = props.filter[key as keyof FilterQuery];
      if (filterValue.id !== FilterAllOption) {
        return true;
      }
    }
    return props.contentType !== ContentTypeFilterAllOption;
  }
}

export interface FilterBarStateProps {
  contentType: ContentTypeFilterValue;
  filter: FilterQuery;
  selectedContentTypeFilterValue: ContentTypeFilterValue;
  searchTerm: string;
}

const mapStateToProps = (state: AppState) => ({
  contentType: state.search.selectedContentTypeFilterValue,
  filter: selectFilterQuery(state),
  searchTerm: state.search.searchTerm,
  selectedContentTypeFilterValue: state.search.selectedContentTypeFilterValue,
});

export interface FilterBarDispatchProps {
  readonly updateFilter: (id: string, value: string, filterType: FilterTypes) => void;
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateFilter,
    },
    dispatch
  );

export const ConnectedFilterBar: React.ComponentClass<{}> = connect(mapStateToProps, mapDispatchToProps)(FilterBar);
